import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import _debounce from "lodash/debounce";
import { Instagram, Tiktok, Youtube, Linkedin, Pinterest, Headphones } from "react-bootstrap-icons";
import axiosService from "../../../../utils/AxiosService";
import { toastSuccess, toastError } from "../../../FormComponents/Toasts";
import ReactSelect from "../../../FormComponents/ReactSelect";

const PROVIDERS = [
  { value: "instagram", Icon: Instagram, className: "text-pink-600 dark:text-pink-400" },
  { value: "tiktok", Icon: Tiktok, className: "text-black dark:text-white" },
  { value: "youtube", Icon: Youtube, className: "text-red-500 dark:text-red-400" },
  { value: "linkedin", Icon: Linkedin, className: "text-rb-linkedin dark:text-blue-400" },
  { value: "pinterest", Icon: Pinterest, className: "text-red-700 dark:text-red-400" },
  { value: "podcast", Icon: Headphones, className: "text-gray-700 dark:text-gray-300" },
]

// Matches AuthenticationSearch#paginate's .per(20) on the backend - used to tell
// whether a page came back full (there may be more to load on scroll).
const PAGE_SIZE = 20

export default observer(function AttachExistingAuthenticationForm({ setIsOpen, infId }) {
  const [provider, setProvider] = useState('')
  const [providerAuthOptions, setProviderAuthOptions] = useState([])
  const [providerAuthUsername, setProviderAuthUsername] = useState('')
  const [providerAuthPage, setProviderAuthPage] = useState(1)
  const [hasMoreProviderAuth, setHasMoreProviderAuth] = useState(false)
  const [selectedAuth, setSelectedAuth] = useState(null)
  const [influencerOptions, setInfluencerOptions] = useState([])
  const [targetInfluencerId, setTargetInfluencerId] = useState(infId || '')
  const [pendingConfirmation, setPendingConfirmation] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (infId) return
    axiosService.get('/api/project_manager/influencers')
      .then((influencers) => setInfluencerOptions(influencers.map(influencer => ({ label: influencer.full_name, value: influencer.id }))))
      .catch(({ data: { error } }) => toastError(error))
  }, [infId])

  const loadAuthenticationsByProvider = (nextProvider, username, page = 1) => {
    if (!nextProvider) { setProviderAuthOptions([]); setHasMoreProviderAuth(false); return }
    const search = { provider: nextProvider, page, order: 'username' }
    if (username) search.username = username
    axiosService.get('/api/project_manager/authentication_search', { params: { search } })
      .then(({ authentications, total_count }) => {
        const opts = (authentications || []).map(a => ({ label: a.username || `#${a.id}`, value: a.id }))
        setProviderAuthOptions(prev => (page === 1 ? opts : [...prev, ...opts]))
        setProviderAuthUsername(username || '')
        setProviderAuthPage(page)
        setHasMoreProviderAuth(page * PAGE_SIZE < total_count)
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  const searchAuthenticationsByProvider = useCallback(_debounce((nextProvider, username) => loadAuthenticationsByProvider(nextProvider, username), 300), [])

  const loadMoreProviderAuth = () => {
    if (!hasMoreProviderAuth) return
    loadAuthenticationsByProvider(provider, providerAuthUsername, providerAuthPage + 1)
  }

  const selectProvider = (nextProvider) => {
    setProvider(nextProvider)
    setSelectedAuth(null)
    loadAuthenticationsByProvider(nextProvider)
  }

  const attach = (confirmActiveCampaignMove) => {
    if (!selectedAuth?.value) return toastError('Please select an account')
    if (!targetInfluencerId) return toastError('Please select an Influencer')

    setIsSubmitting(true)
    axiosService.patch(`/api/project_manager/authentications/${selectedAuth.value}/change_influencer`, {
      authentication: { influencer_id: targetInfluencerId, confirm_active_campaign_move: confirmActiveCampaignMove }
    })
      .then(() => {
        toastSuccess('Account attached to influencer')
        setIsOpen(false)
        window.location.reload()
      })
      .catch(({ data }) => {
        if (data?.active_campaigns?.length) {
          setPendingConfirmation({ activeCampaigns: data.active_campaigns })
        } else {
          toastError(data?.error)
        }
      })
      .finally(() => setIsSubmitting(false))
  }

  if (pendingConfirmation) {
    return (
      <div className="p-4">
        <p className="font-bold dark:text-white">This account is linked to active campaign(s):</p>
        <ul className="list-disc pl-5 my-2 dark:text-white">
          {pendingConfirmation.activeCampaigns.map(campaign => <li key={campaign.id}>{campaign.name}</li>)}
        </ul>
        <p className="dark:text-white">Moving it will move that campaign history to the new influencer too. Continue?</p>
        <div className="flex justify-between pt-4">
          <button
            type="button"
            className="rb-btn-cancel font-bold text-md p-2 px-4 rounded-lg disabled:opacity-50"
            disabled={isSubmitting}
            onClick={() => setPendingConfirmation(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="flex items-center gap-2 bg-rb-green text-white rounded-lg p-2 px-4 text-center active:shadow-outer active:shadow-rb-green border-rb-green dark:text-rb-dark-mode disabled:opacity-50"
            disabled={isSubmitting}
            onClick={() => { setPendingConfirmation(null); attach(true) }}
          >
            Yes, move it anyway
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <div className="form-item-container w-full pt-4">
        <p className="text-sm pl-4 pb-1 dark:text-white">Provider</p>
        <span role="group" className="ml-4 inline-flex items-center gap-8 rounded-lg bg-rb-grey dark:bg-rb-dark-mode-lighter p-4">
          {PROVIDERS.map(({ value, Icon, className }) => (
            <label key={value} className={`${provider === value ? 'bg-rb-soft-green dark:bg-rb-green/20 border-rb-green dark:border-rb-green' : 'bg-white dark:bg-rb-dark-mode'} cursor-pointer ${className} w-12 h-12 flex items-center justify-center border-1 dark:border-rb-dark-mode-lighter rounded-md`}>
              <input type="radio" name="attach_provider" value={value} className="hidden" checked={provider === value} onChange={() => selectProvider(value)} />
              <Icon />
            </label>
          ))}
        </span>
      </div>

      <div className="form-item-container w-full px-4 mt-4">
        <ReactSelect
          label="Select Account"
          name="selected_authentication"
          options={providerAuthOptions}
          value={selectedAuth?.value}
          filterOption={null}
          onInputChange={(inputValue, { action }) => {
            if (action !== 'input-change') return
            searchAuthenticationsByProvider(provider, inputValue)
          }}
          onMenuScrollToBottom={loadMoreProviderAuth}
          onChange={opt => setSelectedAuth(opt)}
          placeholder={provider ? `Search ${provider} accounts` : 'Select a provider first'}
        />
      </div>

      {!infId && (
        <div className="form-item-container w-full px-4 mt-4">
          <ReactSelect
            label="Influencer Record"
            name="target_influencer_id"
            options={influencerOptions}
            value={targetInfluencerId}
            onChange={influencer => setTargetInfluencerId(influencer?.value || '')}
            placeholder="Select an influencer to attach this account to"
          />
        </div>
      )}

      <div className="flex justify-between pt-4 bg-white dark:bg-rb-dark-mode mt-4">
        <button
          type="button"
          className="rb-btn-cancel font-bold text-md p-2 px-4 rounded-lg disabled:opacity-50"
          disabled={isSubmitting}
          onClick={() => setIsOpen(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ml-auto flex items-center gap-2 bg-rb-green text-white rounded-lg p-2 px-4 text-center active:shadow-outer active:shadow-rb-green border-rb-green dark:text-rb-dark-mode disabled:opacity-50"
          disabled={!selectedAuth?.value || !targetInfluencerId || isSubmitting}
          onClick={() => attach(false)}
        >
          {isSubmitting ? 'Attaching...' : 'Attach to influencer'}
        </button>
      </div>
    </div>
  )
})
